import type { Asset, AssetId, Concept, ConceptId, LabelId } from "@storytell/types";
import { batch } from "solid-js";
import { createStore, reconcile } from "solid-js/store";

type RelationshipsCache = {
  concepts: {
    relatedAssets: Record<ConceptId, AssetId[] | undefined>;
    relatedLabels: Record<ConceptId, LabelId[] | undefined>;
  };
  assets: {
    relatedConcepts: Record<AssetId, ConceptId[] | undefined>;
    relatedLabels: Record<AssetId, LabelId[] | undefined>;
  };
  labels: {
    relatedAssets: Record<LabelId, AssetId[] | undefined>;
    relatedConcepts: Record<LabelId, ConceptId[] | undefined>;
  };
};

const initialStore = (): RelationshipsCache => ({
  concepts: {
    relatedAssets: {},
    relatedLabels: {},
  },
  assets: {
    relatedConcepts: {},
    relatedLabels: {},
  },
  labels: {
    relatedConcepts: {},
    relatedAssets: {},
  },
});

const pushCreateUnique = <Id extends string, Related>(
  id: Id,
  record: Record<Id, Related[] | undefined>,
  value: Related,
) => {
  if (!record[id]) {
    record[id] = [];
  }

  if (!record[id].includes(value)) {
    record[id].push(value);
  }
};
export const buildRelationshipsCache = () => {
  const [store, setStore] = createStore<RelationshipsCache>({
    concepts: {
      relatedAssets: {},
      relatedLabels: {},
    },
    assets: {
      relatedConcepts: {},
      relatedLabels: {},
    },
    labels: {
      relatedConcepts: {},
      relatedAssets: {},
    },
  });

  // This assumes the values are coming from the sync endpoint,
  // which is the source of truth
  const rebuildFullCache = ({ assets, concepts }: { assets: Asset[]; concepts: Concept[] }) => {
    const ns = initialStore();

    for (const asset of assets) {
      const uniqueLabels = Array.from(new Set(asset.labels || []));
      ns.assets.relatedLabels[asset.id] = uniqueLabels;
      for (const label of uniqueLabels) {
        pushCreateUnique(label, ns.labels.relatedAssets, asset.id);
      }
    }
    const newStore = rebuildRelatedLabels(ns, concepts);
    setStore(reconcile(newStore, { merge: true }));
  };

  // Mutates a mutStore instance in place, also returns it
  const rebuildRelatedLabels = (mutStore: RelationshipsCache, concepts: Concept[]) => {
    for (const concept of concepts) {
      const relatedLabels = new Set<LabelId>();
      const relatedAssets = new Set<AssetId>();
      for (const seed of concept.seeds || []) {
        relatedAssets.add(seed.assetId as AssetId);
        pushCreateUnique(seed.assetId as AssetId, mutStore.assets.relatedConcepts, concept.id);
        for (const label of mutStore.assets.relatedLabels[seed.assetId as AssetId] || []) {
          relatedLabels.add(label);
          pushCreateUnique(label, mutStore.labels.relatedConcepts, concept.id);
        }
      }
      mutStore.concepts.relatedLabels[concept.id] = Array.from(relatedLabels);
      mutStore.concepts.relatedAssets[concept.id] = Array.from(relatedAssets);
    }

    return mutStore;
  };
  const assetLabelDeleted = (assetId: AssetId, labelId: LabelId, concepts: Concept[]) => {
    const newStore = initialStore();
    newStore.assets.relatedLabels = {
      ...store.assets.relatedLabels,
      [assetId]: store.assets.relatedLabels[assetId]?.filter((l) => l !== labelId),
    };
    newStore.labels.relatedAssets = {
      ...store.labels.relatedAssets,
      [labelId]: store.labels.relatedAssets[labelId]?.filter((a) => a !== assetId),
    };
    rebuildRelatedLabels(newStore, concepts);
    setStore(reconcile(newStore, { merge: true }));
  };
  const assetLabelAdded = (assetId: AssetId, labelId: LabelId, concepts: Concept[]) => {
    const newStore = initialStore();
    newStore.assets.relatedLabels = {
      ...store.assets.relatedLabels,
      [assetId]: Array.from(new Set([...(store.assets.relatedLabels[assetId] ?? []), labelId])),
    };
    newStore.labels.relatedAssets = {
      ...store.labels.relatedAssets,
      [labelId]: Array.from(new Set([...(store.labels.relatedAssets[labelId] ?? []), assetId])),
    };
    rebuildRelatedLabels(newStore, concepts);
    setStore(reconcile(newStore, { merge: true }));
  };

  const getConceptRelatedLabels = (id: ConceptId) => store.concepts.relatedLabels[id] || [];
  const getConceptRelatedAssets = (id: ConceptId) => store.concepts.relatedAssets[id] || [];
  const getAssetRelatedLabels = (id: AssetId) => store.assets.relatedLabels[id] || [];
  const getAssetRelatedConcepts = (id: AssetId) => store.assets.relatedConcepts[id] || [];
  const getLabelRelatedAssets = (id: LabelId) => store.labels.relatedAssets[id] || [];
  const getLabelRelatedConcepts = (id: LabelId) => store.labels.relatedConcepts[id] || [];

  const getAssetHasLabel = (assetId: AssetId, labelId: LabelId) => getAssetRelatedLabels(assetId).includes(labelId);
  const getConceptHasLabel = (conceptId: ConceptId, labelId: LabelId) =>
    getConceptRelatedLabels(conceptId).includes(labelId);
  const getLabelHasAsset = (labelId: LabelId, assetId: AssetId) => getLabelRelatedAssets(labelId).includes(assetId);
  const getLabelHasConcept = (labelId: LabelId, conceptId: ConceptId) =>
    getLabelRelatedConcepts(labelId).includes(conceptId);
  const getAssetHasConcept = (assetId: AssetId, conceptId: ConceptId) =>
    getAssetRelatedConcepts(assetId).includes(conceptId);
  const getConceptHasAsset = (conceptId: ConceptId, assetId: AssetId) =>
    getConceptRelatedAssets(conceptId).includes(assetId);

  const assetDeleted = (assetId: AssetId, concepts: Concept[]) => {
    const newStore = initialStore();
    newStore.assets.relatedLabels = {
      ...store.assets.relatedLabels,
    };
    delete newStore.assets.relatedLabels[assetId];
    newStore.labels.relatedAssets = {
      ...store.labels.relatedAssets,
    };
    for (const labelId of Object.keys(newStore.labels.relatedAssets)) {
      newStore.labels.relatedAssets[labelId as LabelId] = newStore.labels.relatedAssets[labelId as LabelId]?.filter(
        (a) => a !== assetId,
      );
    }
    rebuildRelatedLabels(newStore, concepts);
    setStore(reconcile(newStore, { merge: true }));
  };

  return {
    rebuildFullCache,
    getConceptRelatedAssets,
    getConceptRelatedLabels,
    getAssetRelatedConcepts,
    getAssetRelatedLabels,
    getLabelRelatedAssets,
    getLabelRelatedConcepts,
    assetLabelDeleted,
    assetLabelAdded,
    getAssetHasLabel,
    getConceptHasLabel,
    getLabelHasAsset,
    getLabelHasConcept,
    getAssetHasConcept,
    getConceptHasAsset,
    assetDeleted,
  };
};
